import { createContext, useContext } from 'react';
import type { Table as TanstackTable } from '@tanstack/react-table';
import type { Dispatch, SetStateAction } from 'react';
import type {
  TableConfig,
  TableEditableState,
  TableState,
  RowSelection,
  TableVariant,
  TableRowBackgroundContext,
} from '../../types';

// ──────────────────────────────────────────────
// 1. Stable context – values that rarely change
// ──────────────────────────────────────────────
export type StableContextValue = {
  config: TableConfig;
  setGlobalFilter: Dispatch<SetStateAction<string>>;
  onTableStateChange?: (state: TableState) => void;
  variant?: TableVariant;
  expandedRowBackgroundColor?: string;
  getRowBackground?: (ctx: TableRowBackgroundContext<unknown>) => string | undefined;
};

// eslint-disable-next-line
const StableContext = createContext<StableContextValue | null>(null);

export function useStableContext(): StableContextValue {
  const ctx = useContext(StableContext);
  if (ctx === null)
    throw new Error('useStableContext must be used within an OptimizedTableProvider');
  return ctx;
}

// ──────────────────────────────────────────────
// 2. Table-instance context – changes on data / sort / filter / pagination
// ──────────────────────────────────────────────
export type TableInstanceContextValue<T = unknown> = {
  // eslint-disable-next-line
  table: TanstackTable<T>;
  globalFilter: string;
  rowSelection: RowSelection;
};

// eslint-disable-next-line
const TableInstanceContext = createContext<TableInstanceContextValue<any> | null>(null);

export function useTableInstance<T = unknown>(): TableInstanceContextValue<T> {
  const ctx = useContext(TableInstanceContext);
  if (ctx === null)
    throw new Error('useTableInstance must be used within an OptimizedTableProvider');
  return ctx as TableInstanceContextValue<T>;
}

// ──────────────────────────────────────────────
// 3. Editable context – changes only during cell editing
// ──────────────────────────────────────────────
export type SetEditableState = (state: TableEditableState) => void;
export type EditableContextValue = {
  editableState: TableEditableState;
  setEditableState: SetEditableState;
};

const EditableContext = createContext<EditableContextValue | null>(null);

/** Provider that only supplies editable context. Used by OptimizedTable outer so inner can stay memo. */
export const EditableOnlyProvider = ({
  value,
  children,
}: {
  value: EditableContextValue;
  children: React.ReactNode;
}) => <EditableContext.Provider value={value}>{children}</EditableContext.Provider>;

export function useEditableContext(): EditableContextValue {
  const ctx = useContext(EditableContext);
  if (ctx === null)
    throw new Error('useEditableContext must be used within an OptimizedTableProvider');
  return ctx;
}

// ──────────────────────────────────────────────
// 4. Scroll context – changes on every scroll event
// ──────────────────────────────────────────────
export type ScrollContextValue = {
  isScrolling: boolean;
};

const ScrollContext = createContext<ScrollContextValue | null>(null);

export function useScrollContext(): ScrollContextValue {
  const ctx = useContext(ScrollContext);
  if (ctx === null)
    throw new Error('useScrollContext must be used within an OptimizedTableProvider');
  return ctx;
}

/**
 * Optional scroll context – returns null when not inside a provider.
 * Used by TableTooltip which may render outside the table.
 */
export function useScrollContextOptional(): ScrollContextValue | null {
  return useContext(ScrollContext);
}

/**
 * Optional editable context – returns null when not inside a provider.
 * Used by TableTooltip which may render outside the table.
 */
export function useEditableContextOptional(): EditableContextValue | null {
  return useContext(EditableContext);
}

// ──────────────────────────────────────────────
// TableCoreProvider – stable, tableInstance, scroll only (editable from EditableOnlyProvider above)
// ──────────────────────────────────────────────
export type TableCoreProviderProps = {
  children: React.ReactNode;
  stable: StableContextValue;
  tableInstance: TableInstanceContextValue;
  scroll: ScrollContextValue;
};

export const TableCoreProvider = ({
  children,
  stable,
  tableInstance,
  scroll,
}: TableCoreProviderProps) => (
  <StableContext.Provider value={stable}>
    <TableInstanceContext.Provider value={tableInstance}>
      <ScrollContext.Provider value={scroll}>{children}</ScrollContext.Provider>
    </TableInstanceContext.Provider>
  </StableContext.Provider>
);

// ──────────────────────────────────────────────
// Combined provider – nests all 4 contexts (backward compat / tests)
// ──────────────────────────────────────────────
export type SplitTableProviderProps = {
  children: React.ReactNode;
  stable: StableContextValue;
  tableInstance: TableInstanceContextValue;
  editable: EditableContextValue;
  scroll: ScrollContextValue;
};

export const SplitTableProvider = ({
  children,
  stable,
  tableInstance,
  editable,
  scroll,
}: SplitTableProviderProps) => (
  <StableContext.Provider value={stable}>
    <TableInstanceContext.Provider value={tableInstance}>
      <EditableContext.Provider value={editable}>
        <ScrollContext.Provider value={scroll}>{children}</ScrollContext.Provider>
      </EditableContext.Provider>
    </TableInstanceContext.Provider>
  </StableContext.Provider>
);

// ──────────────────────────────────────────────
// Backward-compat hooks (reads all contexts)
// ──────────────────────────────────────────────

/** Convenience hook that merges all four contexts into the legacy shape. */
export function useTableContext<T = unknown>() {
  const { config, setGlobalFilter, onTableStateChange } = useStableContext();
  const { table, globalFilter } = useTableInstance<T>();
  const { editableState, setEditableState } = useEditableContext();
  const { isScrolling } = useScrollContext();
  return {
    config,
    table,
    globalFilter,
    setGlobalFilter,
    onTableStateChange,
    editableState,
    setEditableState,
    isScrolling,
  };
}

/** Optional version – returns null when outside provider. */
export function useTableContextOptional<T = unknown>() {
  const stable = useContext(StableContext);
  const instance = useContext(TableInstanceContext) as TableInstanceContextValue<T> | null;
  const editable = useContext(EditableContext);
  const scroll = useContext(ScrollContext);
  if (!stable || !instance || !editable || !scroll) return null;
  return {
    config: stable.config,
    table: instance.table,
    globalFilter: instance.globalFilter,
    setGlobalFilter: stable.setGlobalFilter,
    onTableStateChange: stable.onTableStateChange,
    editableState: editable.editableState,
    setEditableState: editable.setEditableState,
    isScrolling: scroll.isScrolling,
  };
}
